import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useCMS } from "@/lib/CMSContext";
import { SectionTracker } from "./SectionTracker";

const AVATAR_COLORS = [
  "bg-primary/80",
  "bg-accent/80",
  "bg-emerald-500/80",
  "bg-violet-500/80",
  "bg-rose-500/80",
  "bg-sky-500/80",
];

function getInitials(name: string) {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

const Testimonials = () => {
  const { t } = useLanguage();
  const { cms } = useCMS();
  const testimonials = cms.testimonials;

  return (
    <SectionTracker id="testimonials">
    <section id="testimonials" className="py-20 md:py-28">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-accent font-semibold text-sm uppercase tracking-wider">{t("testimonials_label")}</span>
          <h2 className="font-display text-3xl md:text-4xl text-foreground mt-3 mb-4">
            {t("testimonials_title")}
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {testimonials.map((item, i) => (
            <motion.div
              key={item.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: i * 0.15, duration: 0.5 }}
              className="bg-card rounded-xl p-6 shadow-[var(--shadow-soft)] relative"
            >
              <Quote className="w-8 h-8 text-primary/20 absolute top-4 right-4" aria-hidden="true" />
              <div
                className="flex gap-1 mb-4"
                role="img"
                aria-label={`${item.rating} out of 5 stars`}
              >
                {Array.from({ length: item.rating }).map((_, j) => (
                  <Star key={j} className="w-4 h-4 text-accent fill-accent" aria-hidden="true" />
                ))}
              </div>
              <p className="text-foreground text-sm leading-relaxed mb-6 italic">"{item.text}"</p>
              <div className="flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-full ${AVATAR_COLORS[i % AVATAR_COLORS.length]} flex items-center justify-center text-white text-sm font-semibold shrink-0`}
                  aria-hidden="true"
                >
                  {getInitials(item.name)}
                </div>
                <div>
                  <p className="font-semibold text-foreground text-sm">{item.name}</p>
                  <p className="text-muted-foreground text-xs">{item.location}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
    </SectionTracker>
  );
};

export default Testimonials;
